import { memo, MouseEvent, ReactElement } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { Card } from '@components/card';
import { IconProps } from '@components/icons';
import { IconEmail } from '@components/icons/email';
import { IconForm } from '@components/icons/form';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconSms } from '@components/icons/phone-sms';
import { Stack } from '@components/stack';
import { StatusIndicator, StatusIndicatorGroup } from '@components/status-indicator';
import { Text } from '@components/text';
import { Tooltip } from '@components/tooltip';
import { useScopedTranslate } from '@locale/translate';
import { lineClamp } from '@styles/layout-helpers';
import { pxToRem } from '@utils/converters';

import { useJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerActionGroupAction } from 'api/traveller-actions/requests';
import { ACTION_TYPES, getActionLabel } from 'ui/actions/action-config';
import { getActionRatio, getActionStatus } from 'ui/authenticated/main/helpers';
import { useJourneyContext } from 'ui/journey-builder/base-builder';
import { TravellerNodeActionCard } from 'ui/journey-builder/node-types/node-container';
import { NodeActions, NodeExecutionMethod } from 'ui/traveller-journey-builder/node-types/common';
import { NodeContainer } from 'ui/traveller-journey-builder/node-types/node-container';
import { TravellerJourneyNode } from 'ui/traveller-journey-builder/node-types/types';

import { isComboSendFormError } from './channel-error';
import { useOverlayPreview } from './overlay-preview';

import { isActionError } from '../common';
import { toFormUrl } from '../helpers';
import { buildEmailPreviewPropsFromTravellerAction } from '../send-email/email-preview';
import { PreviewEmailMenuItemView } from '../send-email/preview-email-menu-item';
import { PreviewSmsMenuItemView } from '../send-sms/preview-sms-menu-item';
import { buildSmsPreviewPropsFromTravellerAction } from '../send-sms/sms-preview';
import { getStatusIndicatorVariant } from '../status-indicator-variant';

const ACTION_TYPE = ACTION_TYPES.combo_send_form;

const GroupActionStatus = ({
  action,
  icon,
}: {
  action: TravellerActionGroupAction;
  icon: ReactElement<IconProps>;
}) => {
  const isError = isActionError(action);
  const ratio = getActionRatio(action);
  const status = getActionStatus(action);
  const variant = getStatusIndicatorVariant({
    isSkipped: status === 'skipped',
    isError,
    isCompleted: status === 'completed',
    isInProgress: status === 'in_progress',
  });

  return <StatusIndicator variant={variant} icon={icon} ratio={ratio} />;
};

export const Node = memo((node: TravellerJourneyNode<typeof ACTION_TYPE>) => {
  const { t } = useScopedTranslate(nodeLocale);
  const showOverlayPreview = useOverlayPreview();
  const { journeyActionForms, travellerJourneyId } = useJourneyContext();

  const journeyActionId = node.data.journeyActionId as keyof typeof journeyActionForms;
  const journeyActionForm = journeyActionForms[journeyActionId];

  const emailAction = node.data.groupActions?.find((action) => action.action === 'send_email');
  const smsAction = node.data.groupActions?.find((action) => action.action === 'send_sms');
  const isError = isComboSendFormError(node.data.groupActions);

  const formName = journeyActionForm?.name || t('untitledForm');
  const formId = journeyActionForm?.shortId;
  const formUrl = journeyActionForm?.formUrl;

  const handlePreviewClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (formId) {
      showOverlayPreview({
        formId,
        travellerJourneyId,
      });
    }
  };

  const handleOpenClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (formUrl) {
      window.open(toFormUrl(formUrl), '_blank');
    }
  };

  return (
    <NodeContainer node={node}>
      {journeyActionForm && (
        <TravellerNodeActionCard
          node={node}
          label={getActionLabel(ACTION_TYPE)}
          descriptionText={node.data.description}
          renderBelowDescription={
            <div style={{ paddingTop: pxToRem(8), paddingRight: pxToRem(8) }}>
              <Card spaceY={2} spaceX={4} variant="transparent">
                <Stack align="center" space={3} orientation="horizontal">
                  <IconForm />
                  <Tooltip content={formName}>
                    <NameContainer>
                      <Text font="semibold14" text={formName} />
                    </NameContainer>
                  </Tooltip>
                  <ButtonWrapper>
                    {formUrl ? (
                      <Button
                        variant="ghost"
                        dataTestid="open-form"
                        onClick={handleOpenClick}
                        text={t('open')}
                        iconRight={<IconOpenNewTab size={16} />}
                      />
                    ) : (
                      <Button
                        variant="ghost"
                        dataTestid="preview"
                        onClick={handlePreviewClick}
                        text={t('core.preview')}
                      />
                    )}
                  </ButtonWrapper>
                </Stack>
              </Card>
            </div>
          }
          isError={isError}
        >
          <NodeActions
            node={node}
            overwriteError={isError}
            customExecutionIndicators={
              <StatusIndicatorGroup>
                {emailAction && !emailAction.isDisabled && (
                  <GroupActionStatus icon={<IconEmail />} action={emailAction} />
                )}
                {smsAction && !smsAction.isDisabled && (
                  <GroupActionStatus icon={<IconSms />} action={smsAction} />
                )}
                <NodeExecutionMethod action={node.data} overwriteError={isError} />
              </StatusIndicatorGroup>
            }
            customMenuItems={
              emailAction || smsAction ? (
                <>
                  {emailAction && <PreviewEmailMenuItem actionId={emailAction.shortId} />}
                  {smsAction && <PreviewSmsMenuItem actionId={smsAction.shortId} />}
                </>
              ) : null
            }
          />
        </TravellerNodeActionCard>
      )}
    </NodeContainer>
  );
});

const PreviewEmailMenuItem = ({ actionId }: { actionId: string }) => {
  const { data: fetchedEmailAction } = useJourneyTravellerAction<'send_email'>(actionId);

  return (
    <PreviewEmailMenuItemView
      previewProps={
        fetchedEmailAction
          ? buildEmailPreviewPropsFromTravellerAction(fetchedEmailAction)
          : undefined
      }
    />
  );
};

const PreviewSmsMenuItem = ({ actionId }: { actionId: string }) => {
  const { data: fetchedSmsAction } = useJourneyTravellerAction<'send_sms'>(actionId);

  return (
    <PreviewSmsMenuItemView
      previewProps={
        fetchedSmsAction ? buildSmsPreviewPropsFromTravellerAction(fetchedSmsAction) : undefined
      }
    />
  );
};

const nodeLocale = {
  en: {
    untitledForm: 'Untitled form',
    open: 'Open',
  },
};

const NameContainer = styled.div({
  flexGrow: 1,
  minWidth: 0,
  ...lineClamp('-webkit-box', 2, true),
  wordBreak: 'normal',
});

const ButtonWrapper = styled.div({
  flexShrink: 0,
  whiteSpace: 'nowrap',
});
